import axiosInstance from '../utils/axiosConfig';

/**
 * Template Headquarters ("Template Hub") API.
 * All endpoints live under /api/template-hub/ and are gated on the backend by
 * HasTemplateHubAccess. Cookie auth is handled by the shared axios instance.
 *
 * List endpoints use OptionalPagination — without a ?page param they return a
 * plain array, so asList() tolerates both shapes for safety.
 */
const BASE = '/api/template-hub';

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

export const templateHubService = {
    // --- Cabinet -----------------------------------------------------------
    getTemplates: async ({ module, doc_type, is_active, showInactive } = {}) => {
        const params = new URLSearchParams();
        if (module) params.append('module', module);
        if (doc_type) params.append('doc_type', doc_type);
        if (is_active !== undefined && is_active !== '') params.append('is_active', is_active);
        if (showInactive) params.append('show_inactive', 'true');
        const query = params.toString();
        const response = await axiosInstance.get(`${BASE}/templates/${query ? `?${query}` : ''}`);
        return asList(response.data);
    },

    // --- Content blocks ----------------------------------------------------
    getBlocks: async ({ template, category, doc_type, requires_approval } = {}) => {
        const params = new URLSearchParams();
        if (template) params.append('template', template);
        if (category) params.append('category', category);
        if (doc_type) params.append('doc_type', doc_type);
        if (requires_approval !== undefined && requires_approval !== '') {
            params.append('requires_approval', requires_approval);
        }
        const query = params.toString();
        const response = await axiosInstance.get(`${BASE}/blocks/${query ? `?${query}` : ''}`);
        return asList(response.data);
    },

    getVersions: async (blockId) => {
        const response = await axiosInstance.get(`${BASE}/blocks/${blockId}/versions/`);
        return response.data;
    },

    getHistory: async (blockId) => {
        const response = await axiosInstance.get(`${BASE}/blocks/${blockId}/history/`);
        return response.data;
    },

    // Create a DRAFT version. value matches the block's content_format shape.
    saveDraft: async (blockId, { value, change_note }) => {
        const response = await axiosInstance.post(`${BASE}/blocks/${blockId}/draft/`, {
            value,
            change_note,
        });
        return response.data;
    },

    // Create + submit in one step. Response: { success, message, auto_approved, version }.
    // auto_approved === true means it published immediately (non-legal block).
    submitBlock: async (blockId, { value, change_note }) => {
        const response = await axiosInstance.post(`${BASE}/blocks/${blockId}/submit/`, {
            value,
            change_note,
        });
        return response.data;
    },

    // --- Approvals ---------------------------------------------------------
    // Versions awaiting the CURRENT user's approval (empty array if not an approver).
    getPendingApprovals: async () => {
        const response = await axiosInstance.get(`${BASE}/versions/pending/`);
        return asList(response.data);
    },

    approveVersion: async (versionId, notes = '') => {
        const response = await axiosInstance.post(`${BASE}/versions/${versionId}/approve/`, { notes });
        return response.data;
    },

    rejectVersion: async (versionId, reason) => {
        const response = await axiosInstance.post(`${BASE}/versions/${versionId}/reject/`, { reason });
        return response.data;
    },

    // --- PDF preview -------------------------------------------------------
    // Render a preview PDF (placeholder sample data) for a saved template /
    // entity / version and return an object URL for the blob. The caller OWNS
    // that URL and must URL.revokeObjectURL() it when done to avoid leaks.
    getPreviewObjectUrl: async ({ doc_type, entity_id, version_id, template_type, number_format } = {}) => {
        const params = {};
        if (doc_type) params.doc_type = doc_type;
        if (entity_id) params.entity_id = entity_id;
        if (version_id) params.version_id = version_id;
        // Invoice-family variant selectors — only forwarded when explicitly set.
        if (template_type) params.template_type = template_type;
        if (number_format) params.number_format = number_format;
        const response = await axiosInstance.get(`${BASE}/preview/`, {
            params,
            responseType: 'blob',
        });
        return URL.createObjectURL(response.data);
    },

    // Preview an UNSAVED editor value: overlays `value` for `block_key` for this
    // render only (nothing is persisted). Same object-URL ownership contract.
    getUnsavedPreviewObjectUrl: async ({ doc_type, entity_id, block_key, value } = {}) => {
        const response = await axiosInstance.post(
            `${BASE}/preview/`,
            { doc_type, entity_id: entity_id || null, block_key, value },
            { responseType: 'blob' },
        );
        return URL.createObjectURL(response.data);
    },

    // Preview requests use responseType 'blob', so an axios error carries the
    // JSON {error} body as a Blob rather than a parsed object — read it back as
    // text and JSON.parse to surface the real message.
    parseBlobError: async (err, fallback = 'Could not generate the preview.') => {
        const data = err?.response?.data;
        try {
            if (data instanceof Blob) {
                const parsed = JSON.parse(await data.text());
                return parsed?.error || fallback;
            }
            return data?.error || err?.message || fallback;
        } catch (parseErr) {
            return fallback;
        }
    },
};

export default templateHubService;
